import React, { useState, useEffect, useCallback } from 'react';
import { useChat } from '../contexts/ChatContext';
import { useAuth } from '../contexts/AuthContext';
import MessageComposer from './MessageComposer';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { X, MessageSquare, CheckCheck } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const ThreadPanel = ({ parentMessage, onClose, channelId }) => {
  const { loadMessages, onlineUsers } = useChat();
  const { user } = useAuth();
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReplies = useCallback(async () => {
    setLoading(true);
    try {
      const msgs = await loadMessages(channelId, parentMessage.message_id);
      setReplies(msgs);
    } catch (e) {
      console.error('Load replies error:', e);
    }
    setLoading(false);
  }, [channelId, parentMessage.message_id, loadMessages]);

  useEffect(() => {
    fetchReplies();
  }, [fetchReplies]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="h-full flex flex-col" data-testid="thread-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          <h3 className="text-sm font-semibold" style={{fontFamily: 'Space Grotesk'}}>Thread</h3>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="thread-close-button">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="px-4 py-3">
          {/* Parent Message */}
          <div className="pb-3 mb-3 border-b border-border">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium flex-shrink-0">
                {parentMessage.sender_name?.[0] || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-semibold">{parentMessage.sender_name}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">{formatTime(parentMessage.timestamp)}</span>
                </div>
                <p className="mt-0.5 text-sm leading-6 whitespace-pre-wrap break-words">{parentMessage.content}</p>
              </div>
            </div>
          </div>

          {/* Replies */}
          {loading ? (
            <div className="text-center py-4 text-sm text-muted-foreground">Loading replies...</div>
          ) : replies.length === 0 ? (
            <div className="text-center py-4 text-sm text-muted-foreground">No replies yet. Start the thread!</div>
          ) : (
            <div className="space-y-1">
              {replies.map(reply => {
                const readCount = (reply.read_by || []).filter(id => id !== reply.sender_id).length;
                return (
                  <div key={reply.message_id} className="group px-2 py-2 hover:bg-[hsl(var(--message-hover))] rounded-lg transition-colors duration-150">
                    <div className="flex gap-3">
                      <div className="relative flex-shrink-0">
                        <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium">
                          {reply.sender_name?.[0] || '?'}
                        </div>
                        {onlineUsers[reply.sender_id] && (
                          <div className="absolute -bottom-0.5 -right-0.5 presence-dot presence-online" style={{width:'8px',height:'8px'}} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-medium">{reply.sender_name}</span>
                          <span className="text-xs text-muted-foreground tabular-nums">{formatTime(reply.timestamp)}</span>
                          {readCount > 0 && (
                            <Tooltip>
                              <TooltipTrigger>
                                <CheckCheck className="w-3 h-3 text-primary" />
                              </TooltipTrigger>
                              <TooltipContent>Read by {readCount}</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        <p className="mt-0.5 text-sm leading-5 whitespace-pre-wrap break-words">{reply.content}</p>
                        {reply.code_snippet && (
                          <div className="mt-2 rounded-lg border border-border overflow-hidden max-w-full">
                            <SyntaxHighlighter
                              language={reply.code_snippet.language || 'javascript'}
                              style={oneDark}
                              customStyle={{ margin: 0, borderRadius: 0, fontSize: '12px' }}
                            >
                              {reply.code_snippet.code}
                            </SyntaxHighlighter>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Thread Composer */}
      <MessageComposer
        channelId={channelId}
        parentId={parentMessage.message_id}
        onSent={fetchReplies}
        data-testid="thread-reply-input"
      />
    </div>
  );
};

export default ThreadPanel;
